/**
 * Which folders the workflow watches.
 *
 * The "All folders" option is a standing rule, not a pre-ticked list — a folder
 * created next month is covered by it. The copy says so explicitly, because the
 * difference between it and "tick everything" is invisible on the day you choose.
 */

import { Loader2 } from "lucide-react";
import React from "react";
import type { FolderScope } from "../../types/kpiWorkflow";

export interface FolderOption {
	id: number;
	name: string;
	fileCount: number;
}

interface FolderScopePickerProps {
	folders: FolderOption[];
	/**
	 * The folder tree is fetched beside the page rather than inside it, so the
	 * picker can open before it arrives. Saying "still loading" matters because
	 * the alternative — "No folders yet" — reads as a fact about the account.
	 */
	isLoading?: boolean;
	/**
	 * The fetch failed. Only shown when there is nothing else to show: a failed
	 * refresh keeps the last good list, and a list the user can still tick is
	 * worth more than a banner about it being stale.
	 */
	hasError?: boolean;
	/** Re-fires the folder fetch. Required for the error state to be a way out. */
	onRetry?: () => void;
	scope: FolderScope;
	selectedIds: ReadonlySet<number>;
	onScopeChange: (scope: FolderScope) => void;
	onToggleFolder: (id: number) => void;
}

const FolderScopePicker: React.FC<FolderScopePickerProps> = ({
	folders,
	isLoading = false,
	hasError = false,
	onRetry,
	scope,
	selectedIds,
	onScopeChange,
	onToggleFolder,
}) => (
	<div className="space-y-2">
		<label className="flex items-start gap-2 cursor-pointer">
			<input
				type="radio"
				name="folder-scope"
				className="mt-1 border-gray-300 text-[#008782] focus:ring-[#008782]"
				checked={scope === "all"}
				onChange={() => onScopeChange("all")}
			/>
			<span>
				<span className="text-base text-text-base block">
					All folders — including future ones
				</span>
				<span className="text-xs text-text-muted">
					New folders are picked up automatically as they appear.
				</span>
			</span>
		</label>

		<label className="flex items-start gap-2 cursor-pointer">
			<input
				type="radio"
				name="folder-scope"
				className="mt-1 border-gray-300 text-[#008782] focus:ring-[#008782]"
				checked={scope === "selected"}
				onChange={() => onScopeChange("selected")}
			/>
			<span className="text-base text-text-base">Only the folders I pick</span>
		</label>

		{scope === "selected" && (
			<div className="ml-6 border border-gray-200 rounded-lg max-h-56 overflow-y-auto">
				{isLoading && folders.length === 0 ? (
					<p className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted">
						<Loader2 className="w-4 h-4 animate-spin" aria-hidden />
						Loading your folders…
					</p>
				) : hasError && folders.length === 0 ? (
					<div className="px-3 py-2">
						<p className="text-sm text-text-muted">
							Couldn't load your folders.
						</p>
						{onRetry && (
							<button
								type="button"
								onClick={onRetry}
								className="mt-1 text-sm font-medium text-[#008782] hover:underline"
							>
								Retry
							</button>
						)}
					</div>
				) : folders.length === 0 ? (
					<p className="px-3 py-2 text-sm text-text-muted">
						No folders yet. Upload files or connect a data room first.
					</p>
				) : (
					folders.map((folder) => (
						<label
							key={folder.id}
							className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
						>
							<input
								type="checkbox"
								className="rounded border-gray-300 text-[#008782] focus:ring-[#008782]"
								checked={selectedIds.has(folder.id)}
								onChange={() => onToggleFolder(folder.id)}
							/>
							<span className="text-base text-text-base flex-1 truncate">
								{folder.name}
							</span>
							<span className="text-xs text-text-muted">
								{folder.fileCount} files
							</span>
						</label>
					))
				)}
			</div>
		)}
	</div>
);

export default FolderScopePicker;
